import { useState } from 'react';
import { Table, Button, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaEye, FaStar, FaRegStar } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Paginate from '../../components/Paginate';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

import {
  useAdminGetAllBlogPostsQuery,
  useAdminToggleFeaturedMutation,
} from '../../slices/blogsApiSlice';

export default function AdminBlogsScreen() {
  const { pageNumber } = useParams();
  const page = Number(pageNumber) || 1;
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState('');
  const [keyword, setKeyword] = useState('');

  const { data, isLoading, error } = useAdminGetAllBlogPostsQuery({
    page,
    pageSize: 20,
    status: statusFilter || undefined,
    keyword: keyword || undefined,
  });

  const [toggleApi] = useAdminToggleFeaturedMutation();

  const basePath = `/admin/blogs`;

  if (isLoading) return <Loader />;
  if (error) {
    return <Message variant='danger'>{error?.data?.message || error.error}</Message>;
  }

  const handleToggleFeatured = async (id) => {
    try {
      const result = await toggleApi(id).unwrap();
      toast.success(result.isFeatured ? 'Post featured' : 'Post unfeatured');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update');
    }
  };

  return (
    <>
      <Link
        to={`/admin/home`}
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        ← Back to Settings
      </Link>

      <h1>Manage Blog Posts</h1>

      <div className='d-flex flex-wrap gap-3 mb-3'>
        <Form.Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className='w-auto'
        >
          <option value=''>All Statuses</option>
          <option value='draft'>Draft</option>
          <option value='published'>Published</option>
          <option value='restricted'>Restricted</option>
        </Form.Select>
        <Form.Control
          type='text'
          placeholder='Search by title...'
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className='w-auto'
        />
      </div>

      {data.posts.length === 0 ? (
        <Message>No blog posts found.</Message>
      ) : (
        <Table striped hover responsive className='table-sm'>
          <thead>
            <tr>
              <th>Title</th>
              <th>Author</th>
              <th>Status</th>
              <th>Visibility</th>
              <th>Featured</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.posts.map((post) => (
              <tr key={post._id}>
                <td>
                  <Link to={`/blog/post/${post.slug}`} className='text-decoration-none fw-semibold'>
                    {post.title}
                  </Link>
                </td>
                <td>{post.author?.firstName || 'Unknown'}</td>
                <td>
                  <Badge bg={post.status === 'published' ? 'success' : 'secondary'}>
                    {post.status}
                  </Badge>
                  {post.isRestricted && (
                    <Badge bg='danger' className='ms-1'>
                      Restricted
                    </Badge>
                  )}
                </td>
                <td>
                  <Badge bg={post.isPrivate ? 'secondary' : 'success'}>
                    {post.isPrivate ? 'Private' : 'Public'}
                  </Badge>
                </td>
                <td>
                  <Button
                    size='sm'
                    variant={post.isFeatured ? 'warning' : 'outline-warning'}
                    onClick={() => handleToggleFeatured(post._id)}
                  >
                    {post.isFeatured ? <FaStar /> : <FaRegStar />}
                  </Button>
                </td>
                <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                <td>
                  <Button
                    size='sm'
                    variant='outline-primary'
                    title='Review Post'
                    onClick={() => navigate(`/blog/post/${post.slug}`)}
                  >
                    <FaEye className='me-1' />Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className='d-flex justify-content-center' style={{ margin: '32px 0' }}>
        <Paginate
          totalPages={data?.totalPages}
          currentPage={data?.currentPage}
          basePath={basePath}
          firstPageIsBasePath={true}
        />
      </div>
    </>
  );
}
